<template>
  <div>
    <v-row
      dense
      align="end"
    >
      <v-col cols="6">
        <v-row no-gutters>
          <v-col
            class="mb-2 ml-1 text-body-1"
            cols="12"
          >
            {{ $t('training_builder.configure.dropdown.type.title') }}
          </v-col>
          <v-col>
            <validation-provider
              v-slot="{ errors }"
            >
              <v-select
                v-model="dirtySetParams.TPPSC"
                outlined
                dense
                :error-messages="errors"
                :items="[ { text: $t('training_builder.configure.type.num_of_repetitions'), value: 'NMRP' }, { text: $t('training_builder.configure.type.set_duration'), value: 'STDR' }, { text: $t('training_builder.configure.type.rir'), value: $t('training_builder.configure.type.rir') }]"
              />
            </validation-provider>
          </v-col>
        </v-row>
      </v-col>
      <v-col
        v-if="dirtySetParams.TPPSC"
        cols="6"
      >
        <validation-provider
          v-slot="{ errors }"
        >
          <number-input
            v-if="dirtySetParams.TPPSC === 'NMRP'"
            v-model="dirtySetParams.NMRP"
            outlined
            dense
            :label="$t('training_builder.text_input.no_of_repetitions.title')"
            :placeholder="$t('label.e_g') + ' ' + 10"
            :decimal-precision="0"
            :error-messages="errors"
          />
          <number-input
            v-else-if="dirtySetParams.TPPSC === 'STDR'"
            v-model="dirtySetParams.STDR"
            outlined
            dense
            :label="$t('training_builder.text_input.set_duration.title')"
            :placeholder="$t('label.e_g') + ' ' + 30"
            :decimal-precision="0"
            :error-messages="errors"
          />
          <number-input
            v-else-if="dirtySetParams.TPPSC === 'RIR'"
            v-model="dirtySetParams.RIR"
            outlined
            dense
            :label="$t('training_builder.text.input.rir.title')"
            :placeholder="$t('label.e_g') + ' ' + 10"
            :decimal-precision="0"
            :error-messages="errors"
          />
        </validation-provider>
      </v-col>
    </v-row>

    <v-divider class="mb-6" />

    <v-row
      dense
      align="end"
    >
      <v-col cols="6">
        <v-row no-gutters>
          <v-col
            class="mb-2 ml-1 text-body-1"
            cols="12"
          >
            {{ $t('training_builder.configure.dropdown.weight.title') }}
          </v-col>
          <v-col>
            <validation-provider
              v-slot="{ errors }"
            >
              <v-select
                v-model="dirtySetParams.MTPSC"
                outlined
                dense
                :error-messages="errors"
                :items="[ { text: $t('training_builder.configure.method.weight'), value: 'WGHT' }, { text: $t('training_builder.configure.method.percent_from_rm'), value: 'PERRM' }]"
              />
            </validation-provider>
          </v-col>
        </v-row>
      </v-col>
      <v-col
        v-if="dirtySetParams.MTPSC"
        cols="6"
      >
        <validation-provider
          v-slot="{ errors }"
        >
          <number-input
            v-if="dirtySetParams.MTPSC === 'WGHT'"
            :value="displayedWeight"
            outlined
            dense
            :label="$t('training_builder.text_input.weight.title')"
            :placeholder="$t('label.e_g') + ' ' + 50"
            :suffix="weightSuffix(user.organisation_settings.timezone)"
            :decimal-precision="1"
            :error-messages="errors"
            @input="displayedWeight = $event"
          />
          <number-input
            v-else-if="dirtySetParams.MTPSC === 'PERRM'"
            v-model="dirtySetParams.PERRM"
            outlined
            dense
            :label="$t('training_builder.text_input.percent_from_rm.title')"
            :placeholder="$t('label.e_g') + ' ' + 75"
            suffix="%"
            :decimal-precision="1"
            :error-messages="errors"
          />
        </validation-provider>
      </v-col>
    </v-row>

    <v-divider class="mb-6" />

    <v-row dense>
      <v-col cols="6">
        <minute-second-input
          :value="dirtySetParams.RSSTDR"
          :label="$t('training_builder.text_input.rest_time_after_set')"
          variable="RSSTDR"
          @input="handleDurationInput"
        />
      </v-col>
    </v-row>

    <v-divider class="mb-6" />

    <v-row
      dense
    >
      <v-col cols="6">
        <v-row no-gutters>
          <v-col
            class="mb-2 ml-1 text-body-1"
            cols="12"
          >
            {{ $t('training_builder.configure.dropdown.force_velocity.title') }}
          </v-col>
          <v-col>
            <validation-provider
              v-slot="{ errors }"
            >
              <v-select
                v-model="dirtySetParams.FVCH"
                outlined
                dense
                :placeholder="$t('training_builder.dropdown.joker.placeholder')"
                :error-messages="errors"
                :items="[ { text: $t('status.yes'), value: true }, { text: $t('status.no'), value: false }]"
              />
            </validation-provider>
          </v-col>
        </v-row>
      </v-col>
      <v-col
        v-if="dirtySetParams.FVCH"
        cols="6"
      >
        <v-row no-gutters>
          <v-col
            class="mb-2 ml-1 text-body-1"
            cols="12"
          >
            {{ $t('force_velocity_zone.label') }}
          </v-col>
          <v-col>
            <validation-provider
              v-slot="{ errors }"
            >
              <v-select
                v-model="dirtySetParams.FVZN"
                outlined
                dense
                :placeholder="$t('force_velocity_zone.placeholder')"
                :error-messages="errors"
                :items="[ { text: $t('training_builder.text_input.maximal.title'), value: $t('training_builder.text_input.maximal.title') }, { text: $t('training_builder.text_input.strength_speed.title'), value: $t('training_builder.text_input.strength_speed.title') }, { text: $t('training_builder.text_input.power_balistic'), value: $t('training_builder.text_input.power_balistic') }, { text: $t('training_builder.text_input.speed.title'), value: $t('training_builder.text_input.speed.title') }]"
              />
            </validation-provider>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-divider class="mb-6" />
    <v-row
      dense
    >
      <v-col
        class="mb-2 ml-1 text-body-1"
        cols="12"
      >
        {{ $t('contraction_type.label') }}
      </v-col>
      <v-col cols="6">
        <validation-provider
          v-slot="{ errors }"
        >
          <v-select
            v-model="dirtySetParams.CNTP"
            outlined
            multiple
            dense
            :placeholder="$t('contraction_type.placeholder')"
            :error-messages="errors"
            :items="exercise.tags.filter(tag => tag.tag_id === 'contraction_type').map(tag => ({ text: tag.value, value: tag.value }))"
          />
        </validation-provider>
      </v-col>
      <v-col />
    </v-row>
    <v-row
      dense
    >
      <v-col
        v-if="dirtySetParams.CNTP.indexOf('Iso') > -1"
        cols="6"
      >
        <validation-provider
          v-slot="{ errors }"
        >
          <number-input
            v-model="dirtySetParams.ISDR"
            outlined
            dense
            :label="$t('training_builder.text_input.iso_duration.title')"
            :placeholder="$t('label.e_g') + ' ' + 5"
            suffix="s"
            type="number"
            :decimal-precision="0"
            :error-messages="errors"
          />
        </validation-provider>
      </v-col>
      <v-col
        v-if="dirtySetParams.CNTP.indexOf('Con') > -1"
        cols="6"
      >
        <validation-provider
          v-slot="{ errors }"
        >
          <number-input
            v-model="dirtySetParams.CNDR"
            outlined
            dense
            :label="$t('training_builder.text_input.con_duration.title')"
            :placeholder="$t('label.e_g') + ' ' + 5"
            suffix="s"
            type="number"
            :decimal-precision="0"
            :error-messages="errors"
          />
        </validation-provider>
      </v-col>
      <v-col
        v-if="dirtySetParams.CNTP.indexOf($t('training_builder.contraction_type.ex')) > -1"
        cols="6"
      >
        <validation-provider
          v-slot="{ errors }"
        >
          <number-input
            v-model="dirtySetParams.EXDR"
            outlined
            dense
            :label="$t('training_builder.text_input.ex_duration.title')"
            :placeholder="$t('label.e_g') + ' ' + 5"
            suffix="s"
            type="number"
            :decimal-precision="0"
            :error-messages="errors"
          />
        </validation-provider>
      </v-col>
      <v-col
        v-if="dirtySetParams.CNTP.indexOf($t('training_builder.contraction_type.ssc')) > -1"
        cols="6"
      >
        <validation-provider
          v-slot="{ errors }"
        >
          <number-input
            v-model="dirtySetParams.SSCDR"
            outlined
            dense
            :label="$t('training_builder.text_input.ssc_duration.title')"
            :placeholder="$t('label.e_g') + ' ' + 5"
            suffix="s"
            type="number"
            :decimal-precision="0"
            :error-messages="errors"
          />
        </validation-provider>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import NumberInput from '@/components/helpers/NumberInput.vue'
import { cloneVariables, convertWeightByTimezone, weightSuffix } from '@/components/training-builder/helpers'
import MinuteSecondInput from '@/components/helpers/MinuteSecondInput.vue'
import { cloneDeep } from 'lodash'

export default {
  name: 'StrengthParamsEntry',
  components: { MinuteSecondInput, NumberInput },
  props: {
    exercise: {
      type: Object,
      required: true
    },
    setParams: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      dirtySetParams: cloneVariables(this.setParams),
      internalWeightInKg: null
    }
  },
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    displayedWeight: {
      get() {
        return convertWeightByTimezone(this.internalWeightInKg, this.user.organisation_settings.timezone, true)
      },
      set(value) {
        this.dirtySetParams.WGHT = convertWeightByTimezone(value, this.user.organisation_settings.timezone, false)
      }
    }
  },
  watch: {
    dirtySetParams: {
      deep: true,
      handler: function (newValue) {
        this.$emit('update:setParams', newValue)
      }
    },
    'dirtySetParams.FVCH': function (val) {
      if (val === false) this.dirtySetParams.FVZN = null
    }
  },
  created() {
    if (!this.dirtySetParams.TPPSC) this.dirtySetParams.TPPSC = 'NMRP'
    if (!this.dirtySetParams.MTPSC) this.dirtySetParams.MTPSC = 'WGHT'
    if (this.dirtySetParams.FVCH === undefined || this.dirtySetParams.FVCH === null) this.dirtySetParams.FVCH = false
    if (!this.dirtySetParams.CNTP || this.dirtySetParams.CNTP.length === 0) {
      this.dirtySetParams.CNTP = this.exercise.tags.filter(tag => tag.tag_id === 'contraction_type').map(tag => tag.value)
    }
    this.internalWeightInKg = cloneDeep(this.dirtySetParams.WGHT)
  },
  methods: {
    weightSuffix,
    handleDurationInput(min, sec, variable) {
      this.dirtySetParams[variable] = min * 60 + sec > 0 ? min * 60 + sec : null
    }
  }
}
</script>
